import { resolveChartingRouteEntry } from '@/features/charting-next/models/chartingSources';
import { buildChartingLegacyHref, buildChartingRouteLocation } from '@/features/charting-next/utils/chartingLinks';
import { computed, ref, watch } from '@vue/composition-api';

export function useChartingRouteState({ route, router, contextStore }) {
    const entry = computed(() => resolveChartingRouteEntry(route));
    const tickerSearch = ref('');
    const descriptionSearch = ref('');
    const legacySourceHref = computed(() => buildChartingLegacyHref({ type: entry.value.type, value: entry.value.value }));

    watch(
        () => entry.value.signature,
        (signature) => {
            contextStore.resetForSource(signature);
        },
        { immediate: true }
    );

    watch(
        () => String(route?.query?.tf || '').toUpperCase(),
        (timeframe) => {
            if (['D', 'W', 'M'].includes(timeframe)) {
                contextStore.setResolution(timeframe);
            }
        },
        { immediate: true }
    );

    const openSource = async ({ type = 'finder', value = '', query = {} } = {}) => {
        await router.push(
            buildChartingRouteLocation({
                type,
                value,
                query
            })
        );
    };

    const syncSelectedTickerInRoute = async (ticker) => {
        const normalizedTicker = String(ticker || '')
            .trim()
            .toUpperCase();
        const nextQuery = {
            ...(route?.query || {})
        };

        if (normalizedTicker) {
            nextQuery.ticker = normalizedTicker;
        } else {
            delete nextQuery.ticker;
        }

        await router.replace({
            query: nextQuery
        });
    };

    const setResolution = async (resolution) => {
        contextStore.setResolution(resolution);

        await router.replace({
            query: {
                ...(route?.query || {}),
                tf: contextStore.resolution
            }
        });
    };

    const submitTickerSearch = async () => {
        const normalizedTicker = String(tickerSearch.value || '')
            .trim()
            .toUpperCase();

        if (!normalizedTicker) {
            return;
        }

        await openSource({
            type: 'ticker',
            value: normalizedTicker
        });
    };

    const submitDescriptionSearch = async () => {
        const normalizedTerm = String(descriptionSearch.value || '').trim();

        if (!normalizedTerm) {
            return;
        }

        await openSource({
            type: 'description',
            value: normalizedTerm
        });
    };

    return {
        entry,
        legacySourceHref,
        tickerSearch,
        descriptionSearch,
        openSource,
        syncSelectedTickerInRoute,
        setResolution,
        submitTickerSearch,
        submitDescriptionSearch
    };
}
